import {
  type ColDef,
  type ColDefField,
  type ICellRendererParams,
  type IRowNode,
} from "ag-grid-community"
import { isEqual, min, max, first, lowerCase } from "lodash"
import {
  formatDecimalToPercent,
  formatNumber,
  formatNumberWithSuffix,
  formatSizeValues,
  uniqueValueFormatter,
} from "~/utils/formatting"
import { formatDate, DateFormatType } from "~/utils/datetime"
import HeaderCell from "~/components/reusable/table/headers/HeaderCell"
import DateFilterInput from "~/components/reusable/table/DateFilterInput"
import { UniqueListTooltip } from "~/components/reusable/table/tooltips/UniqueListTooltip"
import { excelNumberCellClass } from "~/utils/excelStyles"
import { compareSizeDimensionValues } from "~/utils/sizeDimensions"
import type { ProductTableColumn } from "~/constants/productTableColumns"
import { sortSizesComparator } from "~/utils/sizeSplit"
import {
  getColumnNaming,
  type ColumnNamingId,
} from "~/components/planning/ProductTable/utils/columnUtils"
import {
  confidenceLevels,
  HISTORIC_DATA_LABEL,
  type NpiConfidenceLevel,
} from "~/utils/sizeSplitConfidence"
import { ConfidenceBadge } from "~/components/reusable/ConfidenceBadge"

// Base configuration interface that extends ColDef
export interface ColumnTemplateConfig<T = any> extends Partial<ColDef<T>> {
  colId?: ColumnNamingId

  // Common options
  /**
   * No `"right"`: Hakio left-aligns every cell, numbers included, unlike tools that right-align
   * figures. See design-system/APPROVED.md § Table system.
   */
  alignment?: "left" | "center"
  sortingOrder?: ("asc" | "desc" | null)[]
  filter?: boolean | string

  // Custom options
  fixedWidth?: number
  headerSubtitle?: string
  headerInfoIcon?: boolean
  /** Vertically center cell content (badges, custom renderers). Action columns center by default. */
  centerContent?: boolean

  // Number specific
  suffix?: string
  decimals?: number
  percentage?: boolean
  useFormatWithSuffix?: boolean // For formatNumberWithSuffix

  // String specific
  hasTooltip?: boolean
  tooltipTitle?: string
  showUniqueCount?: boolean // Show "5 colors" vs just the values
  noUniqueRange?: boolean
  uniqueSuffix?: string
  // Item numbers and ids: the xlsx export writes them as text, so Excel keeps their leading zeros
  isIdentifier?: boolean

  // Date specific
  dateFormat?: DateFormatType

  // Sorting options for using a different field than the one in the column. Both are required when using this.
  sortingField?: string
  sortingType?: "string" | "number" | "date" | "size" | "sizeDimension"

  // Opt out of the default comparator (auto-group columns: a comparator disables group-sort coupling).
  suppressDefaultComparator?: boolean
}

export interface ActionsColumnConfig<T = any>
  extends Partial<Omit<ColumnTemplateConfig<T>, "alignment">> {
  numberOfActions: number
  stickyEnd?: boolean
}

// Helper function to create comparators based on sorting field and type
function createComparator(
  sortingField?: string,
  sortingType?: "string" | "number" | "date" | "size" | "sizeDimension",
) {
  if (!sortingField || !sortingType) return undefined

  return (_valueA: any, _valueB: any, nodeA: any, nodeB: any) => {
    const nodeAData = nodeA.allLeafChildren?.map((child: any) => child.data) ?? [nodeA.data]
    const nodeBData = nodeB.allLeafChildren?.map((child: any) => child.data) ?? [nodeB.data]

    const dataA = first(nodeAData) as any
    const dataB = first(nodeBData) as any
    const fieldValueA = dataA?.[sortingField]
    const fieldValueB = dataB?.[sortingField]

    if (sortingType === "string") {
      return fieldValueA?.localeCompare(fieldValueB ?? "") ?? 0
    }

    if (sortingType === "number") {
      return (fieldValueA ?? 0) - (fieldValueB ?? 0)
    }

    if (sortingType === "date") {
      if (fieldValueA && fieldValueB) {
        return fieldValueA.getTime() - fieldValueB.getTime()
      }
      return 0
    }

    if (sortingType === "size") {
      return sortSizesComparator(fieldValueA ?? "", fieldValueB ?? "")
    }

    if (sortingType === "sizeDimension") {
      return compareWithNullsLast(fieldValueA, fieldValueB, false, compareSizeDimensionValues)
    }

    return 0
  }
}

/** Sorts null/undefined to the bottom (both directions); delegates to `compare` when both present. */
export function compareWithNullsLast<T>(
  a: T | null | undefined,
  b: T | null | undefined,
  isDescending: boolean,
  compare: (a: T, b: T) => number,
): number {
  if (a == null && b == null) return 0
  if (a == null) return isDescending ? -1 : 1
  if (b == null) return isDescending ? 1 : -1
  return compare(a, b)
}

// Size dimension cells hold one value on SKU rows and every distinct value on variant/group rows.
const sizeDimensionCellValues = (value: unknown): string[] =>
  [...new Set([value].flat().filter((v): v is string => typeof v === "string"))].sort(
    compareSizeDimensionValues,
  )

function formatSizeDimensionCell(value: unknown, empty = ""): string {
  const values = sizeDimensionCellValues(value)
  return values.length ? formatSizeValues(values) : empty
}

/** Orders size dimension cells by their smallest value in dimension order, nulls last. */
function compareSizeDimensionCells(a: unknown, b: unknown, isDescending: boolean): number {
  return compareWithNullsLast(
    sizeDimensionCellValues(a)[0],
    sizeDimensionCellValues(b)[0],
    isDescending,
    compareSizeDimensionValues,
  )
}

const sizeOf = (node: IRowNode | null) =>
  (node?.data as { size?: string | null } | undefined)?.size ?? ""

/**
 * Size dimension 1 / 2 column, the same in product, supply plan and purchase order tables: values in
 * dimension order, "-" when empty, and ties in size order so one length lists its waists in order.
 */
export function createSizeDimensionColumn<T>({
  colId,
  ...config
}: ColumnTemplateConfig<T> & {
  colId: ProductTableColumn.SizeDimension1 | ProductTableColumn.SizeDimension2
}): ColDef<T> {
  return createUniqueStringColumn<T>({
    colId,
    field: colId as never,
    filterParams: {
      comparator: (a: string | null, b: string | null) =>
        compareWithNullsLast(a, b, false, compareSizeDimensionValues),
    },
    valueFormatter: ({ value }) => formatSizeDimensionCell(value, "-"),
    comparator: (a: unknown, b: unknown, nodeA, nodeB, isDescending) =>
      compareSizeDimensionCells(a, b, isDescending) ||
      sortSizesComparator(sizeOf(nodeA), sizeOf(nodeB)) * (isDescending ? -1 : 1),
    ...config,
  })
}

/** Locale-aware string sort; orders `aggFunc:"unique"` array cells by their first element, nulls last. */
function defaultStringComparator(
  valueA: unknown,
  valueB: unknown,
  _nodeA: IRowNode,
  _nodeB: IRowNode,
  isDescending: boolean,
): number {
  const resolve = (v: unknown): string | null => {
    if (v == null) return null
    if (Array.isArray(v)) {
      const strs = v.filter((x) => x != null).map(String)
      return strs.length ? strs.reduce((a, b) => (a.localeCompare(b) <= 0 ? a : b)) : null
    }
    return String(v)
  }
  return compareWithNullsLast(resolve(valueA), resolve(valueB), isDescending, (a, b) =>
    a.localeCompare(b),
  )
}

/**
 * Vertically centers cell content: AG Quartz cells are display:block and value cells wrap content in a
 * line-height'd .ag-cell-value, so badges and icon toolbars otherwise top-align. Rule lives in globals.css.
 */
const CenteredCellClass = "ag-cell-vcenter"

function withClass<P>(
  existing: string | string[] | ((params: P) => string | string[] | null | undefined) | undefined,
  className: string,
): string | string[] | ((params: P) => string[]) {
  if (existing == null) return className
  if (typeof existing === "function")
    return (params: P) => {
      const r = existing(params)
      return [className, ...(Array.isArray(r) ? r : r ? [r] : [])]
    }
  return [className, ...(Array.isArray(existing) ? existing : [existing])]
}

/** Prepend {@link CenteredCellClass} to an existing cellClass (string | string[] | fn). */
function withCenteredCellClass(existing: ColDef["cellClass"], enabled = true): ColDef["cellClass"] {
  return enabled ? withClass(existing, CenteredCellClass) : existing
}

const StickyEndClass = "ag-col-sticky-end"

/**
 * Creates a custom column with basic styling and sorting support.
 * Use for columns that don't fit standard number/string/date patterns.
 */
export function createCustomColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  const {
    sortingField,
    sortingType,
    alignment = "left",
    fixedWidth,
    onCellClicked,
    pinned,
    headerSubtitle,
    headerInfoIcon = false,
    centerContent = false,
    tooltipValueGetter,
    // Only the number/string/date templates read it; left in, ag-grid flags it as an unknown prop.
    hasTooltip: _hasTooltip,
    ...colDefOverrides
  } = config

  // Auto-populate header naming from the central registry based on colId. An explicit
  // headerName / headerSubtitle passed in the config still wins (see below).
  const naming = getColumnNaming(config.colId)
  const resolvedSubtitle = headerSubtitle ?? naming?.columnSubtitle

  return {
    cellStyle:
      alignment !== "left" || onCellClicked
        ? { textAlign: alignment, cursor: onCellClicked ? "pointer" : "default" }
        : undefined,
    headerComponentParams:
      resolvedSubtitle || headerInfoIcon
        ? {
            innerHeaderComponent: HeaderCell,
            subtitle: resolvedSubtitle,
            align: alignment,
            infoIcon: headerInfoIcon,
          }
        : undefined,
    comparator: createComparator(sortingField, sortingType),
    minWidth: fixedWidth,
    maxWidth: fixedWidth,
    equals: (v1, v2) => isEqual(v1, v2),
    pinned,
    onCellClicked,
    // registry defaults; explicit `headerName` / `headerTooltip` in colDefOverrides override them
    headerName: naming?.columnTitle,
    headerTooltip: naming?.tooltip,

    ...colDefOverrides,
    // A caller's deprecated `tooltipValueGetter` (even `undefined`) replaces the template's `tooltip`.
    ...("tooltipValueGetter" in config ? { tooltip: tooltipValueGetter } : {}),
    cellClass: withCenteredCellClass(colDefOverrides.cellClass, centerContent),
  }
}

export const ActionsWrapperClassName = "flex gap-3 items-center m-1"

/** Fills the cell so comment buttons sit in its middle, also in columns without `ag-cell-vcenter`. */
export const CommentsCellWrapperClassName = "flex size-full gap-3 items-center justify-center"

/** Stretches AG Grid's wrapper and value spans to the cell so the count badge isn't clipped. */
export const CommentsCellClassName = `${CenteredCellClass} [&_.ag-cell-wrapper]:size-full [&_.ag-cell-value]:w-full`

/** Centres a header label over a {@link CommentsCellWrapperClassName} cell. */
export const CommentsHeaderClassName = "[&_.ag-header-cell-label]:justify-center"

/**
 * Creates an actions column with fixed width based on number of actions.
 * Auto-calculates width: 46px for first action + 40px per additional action.
 * Disables sorting and cell flashing by default.
 */
export function createActionsColumn<T = any>(config: ActionsColumnConfig<T>): ColDef<T> {
  // The fixed width should be 46px for the first action + 40px for any additional actions.
  const {
    fixedWidth = 46 + 40 * (config.numberOfActions - 1),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    numberOfActions,
    stickyEnd = false,
    ...colDefOverrides
  } = config

  return {
    enableCellChangeFlash: false,
    sortable: false,
    minWidth: fixedWidth,
    maxWidth: fixedWidth,
    cellStyle: { paddingLeft: "4px", paddingRight: "4px" },
    pinned: "right",
    ...(stickyEnd && { lockPinned: true }),

    ...colDefOverrides,
    cellClass: stickyEnd
      ? withClass(withCenteredCellClass(colDefOverrides.cellClass), StickyEndClass)
      : withCenteredCellClass(colDefOverrides.cellClass),
    ...(stickyEnd && { headerClass: withClass(colDefOverrides.headerClass, StickyEndClass) }),
  }
}

/**
 * Creates a number column with sum aggregation, left alignment, and number formatting.
 * Supports suffix display, suffix formatting, and number-specific filters.
 */
export function createNumberColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  const {
    suffix,
    decimals = 0,
    percentage = false,
    useFormatWithSuffix = false,
    alignment = "left",
    sortingOrder = ["desc", "asc", null],
    filter = false,
    hasTooltip = true,
    showUniqueCount = true,
    tooltipTitle,
    uniqueSuffix,
    noUniqueRange = false,
    ...colDefOverrides
  } = config

  // No comparator: AG Grid's built-in numeric sort handles numbers (incl. its agg wrapper).
  // Array-valued columns use createUniqueNumberColumn.
  return createCustomColumn({
    sortingOrder,
    filter: filter ? "agNumberColumnFilter" : false,
    aggFunc: "sum",
    cellDataType: "number",
    cellClass: excelNumberCellClass(percentage),
    alignment,
    valueFormatter: (params) => {
      if (params.value == null) return "-"

      if (showUniqueCount && (tooltipTitle || uniqueSuffix)) {
        const text = uniqueValueFormatter(
          params,
          uniqueSuffix ?? lowerCase(tooltipTitle),
          noUniqueRange,
        )
        return text !== "-" ? text + (suffix ? ` ${suffix}` : "") : ""
      }

      if (useFormatWithSuffix) {
        return formatNumberWithSuffix(Number(params.value), decimals) + (suffix ? ` ${suffix}` : "")
      }

      if (percentage) {
        return formatDecimalToPercent(Number(params.value), decimals) + (suffix ? ` ${suffix}` : "")
      }

      return formatNumber(Number(params.value), decimals) + (suffix ? ` ${suffix}` : "")
    },
    tooltipComponent: hasTooltip && tooltipTitle && showUniqueCount ? UniqueListTooltip : undefined,
    tooltipComponentParams:
      hasTooltip && tooltipTitle && showUniqueCount
        ? { title: tooltipTitle, tooltipType: "UniqueList" }
        : undefined,
    tooltip: (params) => {
      if (!hasTooltip) return undefined

      if (showUniqueCount && tooltipTitle) {
        return params.value
      }

      if (useFormatWithSuffix && hasTooltip) {
        return formatNumber(params.value) + (suffix ? ` ${suffix}` : "")
      }

      return undefined
    },

    ...colDefOverrides,
  })
}

/**
 * Creates a string column with unique aggregation, left alignment, and text formatting.
 * Supports tooltips for unique values and text-specific filters.
 */
export function createStringColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  const {
    tooltipTitle,
    hasTooltip = false,
    showUniqueCount = true,
    sortingOrder = ["asc", "desc", null],
    filter = false,
    uniqueSuffix,
    suppressDefaultComparator = false,
    isIdentifier = false,
    ...colDefOverrides
  } = config

  // Inject the default comparator only when the caller hasn't set one another way. Conditional
  // spread, not `comparator: undefined`, so it can't clobber createComparator via the later spread.
  const useDefaultComparator =
    !colDefOverrides.comparator && !colDefOverrides.sortingField && !suppressDefaultComparator

  return createCustomColumn({
    ...(useDefaultComparator ? { comparator: defaultStringComparator } : {}),
    filter: filter ? "agSetColumnFilter" : false,
    aggFunc: "unique",
    cellDataType: "text",
    cellClass: isIdentifier ? ["stringType", "idType"] : "stringType",
    sortingOrder,
    valueFormatter: (params) => {
      if (showUniqueCount && (tooltipTitle || uniqueSuffix)) {
        return uniqueValueFormatter(params, uniqueSuffix ?? lowerCase(tooltipTitle))
      }
      if (!showUniqueCount && Array.isArray(params.value)) {
        return params.value.join(", ")
      }
      return params.value
    },
    tooltipComponent: hasTooltip && tooltipTitle && showUniqueCount ? UniqueListTooltip : undefined,
    tooltipComponentParams:
      hasTooltip && tooltipTitle && showUniqueCount
        ? { title: tooltipTitle, tooltipType: "UniqueList" }
        : undefined,
    tooltip: (params) => (hasTooltip ? params.value : undefined),

    ...colDefOverrides,
  })
}

// By value: each row carries its own Date object for the same day, so reference-based uniq
// counts them twice. Nested group levels pass their children's arrays, hence the flat().
function uniqueDates(values: (Date | Date[] | null | undefined)[]): Date[] | null {
  const byTime = new Map<number, Date>()
  for (const date of values.flat()) if (date) byTime.set(new Date(date).getTime(), date)
  return byTime.size > 0 ? [...byTime.values()] : null
}

type DateCellValue = Date | string | null | undefined

// Group rows under the "unique" aggregation hold a Date[]; comparing arrays with < compares
// their toString() text, so group rows sort by their earliest date instead
function earliestTime(value: DateCellValue | DateCellValue[]): number | null {
  let earliest: number | null = null
  for (const date of Array.isArray(value) ? value : [value]) {
    if (date == null) continue
    const time = new Date(date).getTime()
    if (earliest == null || time < earliest) earliest = time
  }
  return earliest
}

// The filter hands over local midnight of the picked day, cells hold UTC dates. Comparing raw
// timestamps shifts the boundary day east of UTC, so compare calendar days instead.
export function compareFilterDay(filterDate: Date, cellValue: Date | string | null): number {
  if (cellValue == null) return -1
  const cell = new Date(cellValue)
  const cellDay = Date.UTC(cell.getUTCFullYear(), cell.getUTCMonth(), cell.getUTCDate())
  const filterDay = Date.UTC(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate())
  return Math.sign(cellDay - filterDay)
}

/**
 * Creates a date column with unique aggregation, left alignment, and date formatting.
 * Supports custom date formats and date-specific filters.
 */
export function createDateColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  const {
    dateFormat = DateFormatType.FullDateShort,
    sortingOrder = ["desc", "asc", null],
    filter = false,
    aggFunc = "min",
    showUniqueCount = false,
    tooltipTitle = "Dates",
    hasTooltip = false,
    ...colDefOverrides
  } = config

  let finalAggFunc = aggFunc
  if (aggFunc === "unique") {
    finalAggFunc = (params: any) => uniqueDates(params.values)
  } else if (aggFunc === "min") {
    finalAggFunc = (params: any) => min(params.values)
  } else if (aggFunc === "max") {
    finalAggFunc = (params: any) => max(params.values)
  }

  return createCustomColumn({
    sortingOrder,
    filter: filter ? "agDateColumnFilter" : false,
    dateComponent: filter ? DateFilterInput : undefined,
    aggFunc: finalAggFunc,
    cellDataType: "date",
    cellClass: "dateType",
    comparator: (valueA, valueB, _nodeA, _nodeB, isDescending) => {
      const timeA = earliestTime(valueA)
      const timeB = earliestTime(valueB)
      if (timeA == null && timeB == null) return 0
      if (timeA == null) return isDescending ? -1 : 1
      if (timeB == null) return isDescending ? 1 : -1
      return Math.sign(timeA - timeB)
    },
    valueFormatter: (params) => {
      if (!params.value) return "-"
      return params.node?.group
        ? params.value?.length > 1
          ? `${params.value?.length} dates`
          : formatDate(params.value, dateFormat)
        : formatDate(params.value, dateFormat)
    },
    tooltipComponent: hasTooltip && tooltipTitle && showUniqueCount ? UniqueListTooltip : undefined,
    tooltipComponentParams:
      hasTooltip && tooltipTitle && showUniqueCount
        ? { title: tooltipTitle, tooltipType: "UniqueList" }
        : undefined,
    tooltip: (params) => {
      if (!hasTooltip) return undefined
      return params.node?.group && params.value && Array.isArray(params.value)
        ? params.value.map((date: any) => formatDate(date, dateFormat))
        : params.value
          ? formatDate(params.value, dateFormat)
          : undefined
    },
    filterParams: {
      buttons: ["clear"],
      comparator: compareFilterDay,
      filterOptions: [
        "lessThan",
        "lessThanOrEqual",
        "greaterThan",
        "greaterThanOrEqual",
        "inRange",
      ],
    },

    ...colDefOverrides,
  })
}

// Specialized number templates

/**
 * Creates a number column with sum aggregation (default for most numeric data).
 * Use for quantities, amounts, and other values that should be totaled.
 */
export function createSumColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  return createNumberColumn({ aggFunc: "sum", ...config })
}

/**
 * Creates a number column with average aggregation.
 * Use for rates, percentages, and other values that should be averaged.
 */
export function createAverageColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  return createNumberColumn({
    aggFunc: "avg",
    showUniqueCount: false,
    ...config,
  })
}

/**
 * Creates a number column that counts array lengths (e.g., number of SKUs, variants).
 * Use for counting items in arrays rather than summing numeric values.
 */
export function createCountColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  return createNumberColumn({ aggFunc: "sum", ...config })
}

export function createUniqueNumberColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  return createNumberColumn({
    aggFunc: "unique",
    hasTooltip: true,
    showUniqueCount: true,
    comparator: (valueA, valueB, _nodeA, _nodeB, isDescending) => {
      const resolve = (v: unknown) => (Array.isArray(v) ? min(v as number[]) : (v as number | null))
      return compareWithNullsLast(resolve(valueA), resolve(valueB), isDescending, (a, b) =>
        a < b ? -1 : a > b ? 1 : 0,
      )
    },
    ...config,
  })
}

// Specialized string templates
/**
 * Creates a string column with fixed width of 80 and header name of "ID".
 */
export function createIdColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  return createStringColumn({
    fixedWidth: 80,
    sortingOrder: ["desc", "asc", null],
    headerName: "ID",
    isIdentifier: true,
    ...config,
  })
}

/**
 * Auto-group column template. Stays comparator-less so AG Grid's group-sort coupling keeps
 * ordering groups via the rowGroup column. Use `sortingField`/`sortingType` to sort by a leaf
 * field instead; don't pass a value-based comparator (it would disable coupling).
 */
export function createGroupedStringColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  return createStringColumn({
    pinned: "left",
    lockPinned: true,
    suppressDefaultComparator: true,
    ...config,
  })
}

/**
 * Creates a string column with unique aggregation and tooltip support.
 * Use for categorical data where you want to show unique values in tooltips.
 * Remember to set tooltipTitle to show the unique values.
 */
export function createUniqueStringColumn<T = any>(config: ColumnTemplateConfig<T>): ColDef<T> {
  // List aggregates get fresh array refs (and reorder) on every refetch, so AG Grid would
  // flash them on unrelated updates; a list-cell flash means nothing. Re-enable per caller.
  return createStringColumn({
    hasTooltip: true,
    aggFunc: "unique",
    enableCellChangeFlash: false,
    ...config,
  })
}

interface SizeSplitEntry {
  sizeSplitTrust?: string | null
  isNpi?: boolean | null
  variantStockoutImputedFraction?: number | null
  skuStockoutImputedFraction?: number | null
  imputedSkus?: { size: string; fraction: number }[] | null
  size?: string | null
  variantName?: string | null
}

type ConfidenceCounts = Record<NpiConfidenceLevel, number>

function getConfidenceCounts(node: IRowNode, isDescending: boolean) {
  const entries: SizeSplitEntry[] = node.allLeafChildren?.map((n) => n.data) ?? []
  const counts: ConfidenceCounts = { High: 0, Medium: 0, Low: 0, "Very Low": 0 }
  let historicCount = 0
  for (const e of entries) {
    if (!e?.isNpi) {
      historicCount++
    } else {
      const t = e?.sizeSplitTrust
      if (t && t in counts) counts[t as keyof ConfidenceCounts]++
    }
  }
  return isDescending
    ? [counts.High + historicCount, counts.Medium, counts.Low, counts["Very Low"]]
    : [counts["Very Low"], counts.Low, counts.Medium, counts.High + historicCount]
}

export function createSizeSplitConfidenceColumn<T extends SizeSplitEntry>(config: {
  colId: ColumnNamingId
  isHigherAggregation: boolean
  getEntries: (node: IRowNode<T>) => T[]
}): ColDef<T> {
  const { colId, isHigherAggregation, getEntries } = config

  return createStringColumn<T>({
    colId,
    field: "sizeSplitTrust" as ColDefField<T>,
    headerName: "Size Split Confidence",
    minWidth: 200,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
    },
    cellRenderer: (params: ICellRendererParams<T>) => {
      if (params.node?.footer) return null

      const toImputedSizes = (subset: T[]) =>
        subset.flatMap((e) => {
          if (e?.skuStockoutImputedFraction != null) {
            return [
              {
                size: e.size ?? "",
                fraction: e.skuStockoutImputedFraction,
                variantName: e.variantName ?? "",
              },
            ]
          }
          return (e?.imputedSkus ?? []).map((s) => ({ ...s, variantName: e?.variantName ?? "" }))
        })

      if (isHigherAggregation && params.node?.group) {
        const entries = getEntries(params.node)
        let historicCount = 0
        const npiCounts: Record<string, number> = {}
        for (const entry of entries) {
          if (!entry?.isNpi) {
            historicCount++
          } else {
            const t = entry?.sizeSplitTrust
            if (t) npiCounts[t] = (npiCounts[t] ?? 0) + 1
          }
        }
        const npiSorted = confidenceLevels.filter((k) => npiCounts[k])
        if (historicCount === 0 && npiSorted.length === 0) return null
        const showCount = (historicCount > 0 ? 1 : 0) + npiSorted.length > 1
        const historicImputedSizes = toImputedSizes(entries.filter((e) => !e?.isNpi))
        return (
          <div className="flex items-center gap-1.5 min-w-0">
            {historicCount > 0 && (
              <ConfidenceBadge
                isHistoric
                short
                count={showCount ? historicCount : undefined}
                imputedSizes={historicImputedSizes.length > 0 ? historicImputedSizes : undefined}
              />
            )}
            {npiSorted.map((trust) => {
              const npiImputedSizes = toImputedSizes(
                entries.filter((e) => e?.sizeSplitTrust === trust),
              )
              return (
                <ConfidenceBadge
                  key={trust}
                  confidenceScore={trust}
                  short
                  count={showCount ? npiCounts[trust] : undefined}
                  imputedSizes={npiImputedSizes.length > 0 ? npiImputedSizes : undefined}
                />
              )
            })}
          </div>
        )
      }
      const entries = getEntries(params.node)
      const firstEntry = first(entries)
      const imputedSizes = toImputedSizes(entries)
      return (
        <ConfidenceBadge
          isHistoric={!firstEntry?.isNpi}
          confidenceScore={firstEntry?.sizeSplitTrust}
          imputedSizes={imputedSizes.length > 0 ? imputedSizes : undefined}
          imputedFraction={
            imputedSizes.length === 0 ? firstEntry?.variantStockoutImputedFraction : undefined
          }
          short
        />
      )
    },
    aggFunc: (params) => {
      if (params.rowNode?.level === -1) return null
      return params.values?.[0] ?? null
    },
    valueGetter: (params) => {
      if (params.node?.footer) return ""
      if (isHigherAggregation && params.node?.group) return "Multiple Values"
      if (!params.data?.isNpi) return HISTORIC_DATA_LABEL
      return params.data?.sizeSplitTrust ?? ""
    },
    comparator: (valueA: string, valueB: string, nodeA, nodeB, isDescending) => {
      if (valueA === "" && valueB === "") return 0
      if (valueA === "") return isDescending ? -1 : 1
      if (valueB === "") return isDescending ? 1 : -1
      const order: Record<string, number> = {
        [HISTORIC_DATA_LABEL]: 3,
        High: 3,
        Medium: 2,
        Low: 1,
        "Very Low": 0,
      }
      const isGroupA = nodeA?.group
      const isGroupB = nodeB?.group
      if (isGroupA || isGroupB) {
        const a = isGroupA ? getConfidenceCounts(nodeA, isDescending) : null
        const b = isGroupB ? getConfidenceCounts(nodeB, isDescending) : null
        if (a && b) {
          const dir = isDescending ? -1 : 1
          for (let i = 0; i < 4; i++) {
            if (a[i] !== b[i]) return dir * ((b[i] ?? 0) - (a[i] ?? 0))
          }
          return 0
        }
        return isGroupA ? -1 : 1
      }
      const aVal = order[valueA] ?? 0
      const bVal = order[valueB] ?? 0
      return bVal - aVal
    },
  })
}
